/**
 * Embeddable read-only spectator mode.
 *
 * Activated by URL `?spectate=1&agentId=<snakeId>`. When on, the client skips
 * the menu / wallet / payment flow and joins the live arena as an OBSERVER
 * (no snake, no input), following the given snake. Used by byte to embed a
 * live match of its agent in an <iframe>.
 *
 * This module only reads the URL + hides the interactive DOM chrome. The
 * actual observer join + camera follow live in GameScene / ColyseusClient.
 */

/** True when the page was loaded in spectator mode (`?spectate=1`). */
export function isSpectateMode(): boolean {
  try {
    const v = new URLSearchParams(window.location.search).get('spectate');
    return v === '1' || v === 'on' || v === 'true';
  } catch {
    return false;
  }
}

/** The snake to follow/highlight — the `snakeId` from `POST /api/agents/deploy`.
 *  Optional: null when absent, in which case the camera falls back to the
 *  arena leader. */
export function getSpectateAgentId(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('agentId');
    return id && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
}

/** Interactive DOM chrome overlaid on the canvas that must be hidden when the
 *  game is embedded read-only. The TV bezel + game canvas stay. */
const SPECTATE_HIDE_IDS = [
  'wallet-btn',
  'user-profile',
  'music-switch',
  'sfx-switch',
  'calm-switch',
  'scroll-hint',
  'leaderboard-section',
  'arena-toast',
  'payment-overlay',
  'agent-info-overlay',
];

/** Hide all interactive chrome so the embed shows only the board (+ HUD). */
export function applySpectatorChrome(): void {
  for (const id of SPECTATE_HIDE_IDS) {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = 'none';
      el.style.pointerEvents = 'none';
    }
  }
  document.body.classList.add('spectate-mode');
}
